import {
  Directive,
  HostListener,
  Inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  extractDocumentUrl,
  extractFileInfoFromUrl,
  FileExtension,
  FileResponseType,
  FileService,
  isInternalUrl,
  shouldPreview,
} from '@avenir-client-web/file';
import { APP_CONFIG, BaseAppConfig } from '@avenir-client-web/models';
import { Subject, takeUntil } from 'rxjs';

enum LinkTargetType {
  BLANK = '_blank',
  SELF = '_self',
}

@Directive({
  selector: '[appLinkActionHandler]',
  standalone: true,
})
export class LinkActionHandlerDirective implements OnDestroy {
  @Input() appLinkActionHandler: string;

  @Input() keywords: string[] = [];

  @Input() description: string;

  @Input() id: number;

  private readonly compUnsubscribe$ = new Subject<void>();

  constructor(
    private readonly fileService: FileService,
    private readonly router: Router,
    @Inject(APP_CONFIG) private readonly config: BaseAppConfig
  ) {}

  @HostListener('click')
  onClick(): void {
    this.linkActionHandler();
  }

  ngOnDestroy(): void {
    this.compUnsubscribe$.next();
    this.compUnsubscribe$.complete();
  }

  private linkActionHandler(): void {
    if (!this.appLinkActionHandler) {
      return;
    }

    if (isInternalUrl(this.appLinkActionHandler)) {
      const documentUrlParts = extractDocumentUrl(this.appLinkActionHandler);

      // Open internal link
      if (documentUrlParts?.length !== 2) {
        return this.openLink(LinkTargetType.SELF);
      }

      const extension = this.appLinkActionHandler.split('.').pop();

      // Open document link without filename
      if (!Object.values(FileExtension).includes(extension as FileExtension))
        return this.openLink(LinkTargetType.BLANK);

      const [, fileId, fileName] = extractFileInfoFromUrl(
        this.appLinkActionHandler
      );

      if (shouldPreview(extension as FileExtension)) {
        return this.openLink(LinkTargetType.BLANK);
      }

      return this.downloadFile(
        fileId,
        decodeURIComponent(fileName),
        extension as FileExtension
      );
    } else {
      return this.openLink(LinkTargetType.BLANK);
    }
  }

  private downloadFile(
    fileId: string,
    fileName: string,
    extension: FileExtension
  ): void {
    this.fileService
      .getFileData(fileId, '*', FileResponseType.BLOB)
      .pipe(takeUntil(this.compUnsubscribe$))
      .subscribe(data =>
        this.fileService.downloadFileByBlob(
          data as Blob,
          `${fileName}.${extension}`
        )
      );
  }

  private openLink(target: LinkTargetType): void {
    if (target !== LinkTargetType.SELF) {
      window.open(this.appLinkActionHandler, target);

      return;
    }

    let state = {};

    if (this.keywords?.length > 0) {
      state = { ...state, keys: this.keywords };
    }

    if (this.description?.length > 0) {
      state = {
        ...state,
        description: this.description,
        currentUrl: this.router.url,
      };
    }

    if (this.id) {
      state = { ...state, id: this.id };
    }

    const url = this.trimLocaleFromUrl(this.appLinkActionHandler);

    this.router.navigateByUrl(url, { state });
  }

  /**
   * Normalizes a URL for Angular navigation by removing a leading locale segment.
   *
   * Rules:
   * - Supports absolute and relative URLs.
   * - Detects locale only at the beginning of the pathname.
   * - Matches only `/locale/` format (for configured `supportedLanguages`).
   * - Keeps query params and hash unchanged.
   *
   * Examples:
   * - `http://<host_name>/de/wikis/detail/200` -> `/wikis/detail/200`
   * - `/en/` -> `/`, `/de/` -> `/`
   * - `/wikis/detail/200` -> `/wikis/detail/200` (no locale match)
   */
  private trimLocaleFromUrl(rawUrl: string): string {
    try {
      const parsedUrl = new URL(rawUrl, globalThis.location.origin);
      const supportedLocales = this.getSupportedLocales();

      if (supportedLocales.length === 0) {
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      }

      const pathnameWithoutLocale =
        parsedUrl.pathname.replace(
          this.buildLocaleRegex(supportedLocales),
          ''
        ) || '/';

      return `${pathnameWithoutLocale}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return rawUrl;
    }
  }

  private getSupportedLocales(): string[] {
    return (this.config.supportedLanguages ?? [])
      .map(locale => locale?.trim().toLowerCase())
      .filter(Boolean);
  }

  private buildLocaleRegex(locales: string[]): RegExp {
    const escaped = locales.map(locale =>
      locale.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)
    );

    return new RegExp(String.raw`^\/(${escaped.join('|')})\/`, 'i');
  }
}
